class EcPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.resultsContainer = this.querySelector('[data-ec-predictive-search]');
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isOpen = false;

    if (this.input) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    if (this.resultsContainer && !this.resultsContainer.innerHTML.trim()) {
      this.resultsContainer.setAttribute('data-ec-empty', 'true');
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onInput() {
    if (this.resetButton) {
      this.resetButton.classList.toggle('hidden', !this.input.value.length);
    }
    const newSearchTerm = this.getQuery();
    if (!newSearchTerm.length) {
      this.searchTerm = '';
      this.close(true);
      return;
    }
    this.searchTerm = newSearchTerm;
    this.debouncedFetch = this.debouncedFetch || debounce((term) => this.fetchResults(term), 300);
    this.debouncedFetch(this.searchTerm);
  }

  onFormSubmit(e) {
    if (!this.getQuery().length || this.querySelector('.ec-predictive-search-item[aria-selected="true"] a')) {
      e.preventDefault();
    }
  }

  onFormReset(e) {
    e.preventDefault();
    this.searchTerm = '';
    this.input.value = '';
    this.input.focus();
    if (this.resetButton) this.resetButton.classList.add('hidden');
    this.abortController.abort();
    this.abortController = new AbortController();
    this.close(true);
  }

  onFocus() {
    const current = this.getQuery();
    if (!current.length) return;
    if (this.searchTerm !== current) this.onInput();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.fetchResults(this.searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(e) {
    if (!this.getQuery().length) this.close(true);
    e.preventDefault();
    if (e.code === 'ArrowUp') this.switchOption('up');
    else if (e.code === 'ArrowDown') this.switchOption('down');
    else if (e.code === 'Enter') this.selectOption();
  }

  onKeydown(e) {
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') e.preventDefault();
  }

  switchOption(direction) {
    if (this.getAttribute('open') !== 'true') return;
    const items = Array.from(this.querySelectorAll('.ec-predictive-search-item')).filter(el => el.offsetParent);
    const selected = this.querySelector('.ec-predictive-search-item[aria-selected="true"]');
    let idx = selected ? items.indexOf(selected) : -1;
    if (direction === 'up') idx = idx <= 0 ? items.length - 1 : idx - 1;
    else idx = idx >= items.length - 1 ? 0 : idx + 1;
    items.forEach(el => el.setAttribute('aria-selected', 'false'));
    items[idx]?.setAttribute('aria-selected', 'true');
    this.input?.setAttribute('aria-activedescendant', items[idx]?.id || '');
  }

  selectOption() {
    const selected = this.querySelector('.ec-predictive-search-item[aria-selected="true"] a');
    if (selected) selected.click();
  }

  fetchResults(term) {
    const key = term.replace(/\s+/g, '-').toLowerCase();
    this.setLoading(true);

    if (this.cachedResults[key]) {
      this.renderResults(this.cachedResults[key]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=ec-predictive-search`, {
      signal: this.abortController.signal
    })
      .then(r => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(html => {
        if (this.getQuery() !== term) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-ec-predictive-search');
        const markup = section ? section.innerHTML : '';
        this.cachedResults[key] = markup;
        this.renderResults(markup);
      })
      .catch(err => {
        if (err?.code !== 20) this.close();
      });
  }

  setLoading(loading) {
    if (loading) this.setAttribute('loading', 'true');
    else this.removeAttribute('loading');
    const spinner = this.querySelector('.ec-predictive-search-loading');
    if (spinner) spinner.style.display = loading ? 'block' : 'none';
  }

  renderResults(markup) {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = markup;
    this.setLoading(false);
    const hasItems = !!this.resultsContainer.querySelector('.ec-predictive-search-item');
    if (hasItems) {
      this.setAttribute('results', 'true');
      this.open();
    } else {
      this.removeAttribute('results');
      this.close();
    }
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    const bottom = header?.getBoundingClientRect()?.bottom ?? 0;
    const viewportSpace = Math.max(200, window.innerHeight - bottom);
    return Math.min(viewportSpace, 515);
  }

  open() {
    if (!this.resultsContainer) return;
    this.resultsContainer.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.resultsContainer.removeAttribute('data-ec-empty');
    this.setAttribute('open', 'true');
    this.input?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearTerm = false) {
    if (clearTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.abortController.abort();
    this.abortController = new AbortController();
    this.querySelectorAll('.ec-predictive-search-item[aria-selected="true"]').forEach(el => el.setAttribute('aria-selected', 'false'));
    this.input?.setAttribute('aria-activedescendant', '');
    this.setLoading(false);
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.resultsContainer?.removeAttribute('style');
    if (this.resultsContainer) {
      if (clearTerm) this.resultsContainer.innerHTML = '';
      const hasItems = this.resultsContainer.querySelector('.ec-predictive-search-item, .ec-predictive-search-results');
      if (hasItems) this.resultsContainer.removeAttribute('data-ec-empty');
      else this.resultsContainer.setAttribute('data-ec-empty', 'true');
    }
    this.isOpen = false;
  }
}

customElements.define('ec-predictive-search', EcPredictiveSearch);
